import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import dayjs from 'dayjs'

import { useDispatch, useSelector } from 'react-redux'
import { selectApiKey, selectHash, selectResponseMessages, selectUnreadMessageIndex } from '@selectors'
import { markMessageReadAction } from '@redux/actions/chat'
import { ON_MESSAGE_SEND, ATTEMPT_MESSAGE_SEND_WITH_LOCATION } from '@redux/actions/types'

import useStyles from './chat-message.style'

import { InView } from 'react-intersection-observer'

import ChatBubbleFilter from '@components/chat-bubble-filter/chat-bubble-filter.component'
import ChatMessageFilter from '@components/chat-message-filter/chat-message-filter.component'
import ChatMessageTime from '@components/chat-message-time/chat-message-time.component'
import NewMessageIndicator from '@components/new-message-indicator/new-message-indicator.component'
import ChatResponseMessage from '@components/chat-reponse-message/chat-response-message.component'

import ChatMessageSuggestions from '@components/chat-message-suggestions/chat-message-suggestions.component'

import { useIntl } from 'react-intl'
import { isAfterTimeInterval } from '@utils/helpers'

const ChatMessage = (props) => {
  const intl = useIntl()

  const { index, message, nextMessage, previousMessage } = props

  const { createdAt, guid: messageGuid, id: messageId, location: messageLocation, read, suggestions, type, user } = message

  const { avatar, id } = user

  const location = message.suggestions ? message.suggestions[0].reply.include_location : !!messageLocation

  const dispatch = useDispatch()

  const [isVisible, setIsVisible] = useState(false)

  /* -1 means no suggestion has been tapped; once a user taps suggestion N
   * we lock the whole group (DEV-5207): the tapped one shows a spinner,
   * the rest go disabled until the response message arrives via the
   * Firestore listener and the suggestion group unmounts. */
  const [pressedSuggestionIndex, setPressedSuggestionIndex] = useState(-1)

  const apiKey = useSelector(selectApiKey)

  const userId = useSelector(selectHash)

  const responseMessages = useSelector(selectResponseMessages)
  const responseMessage = responseMessages.find(msg => msg.responseId === message.id)

  const hasResponse = !!responseMessage

  const nextMessageHasImage = nextMessage?.image

  const isReferenceMessage = message?.type === 'reference'

  const unreadMessageIndex = useSelector(selectUnreadMessageIndex)

  const isMe = id === userId

  const showAvatar = () => {
    if (!message.user.avatar) return false

    let { type } = message

    if (message.image) {
      type = 'image'
    }

    switch (type) {
    case 'image':
    case 'reference':
      return false
    default:
      return true
    }
  }

  const classes = useStyles({ ...props, isMe, isReferenceMessage, nextMessageHasImage, showAvatar: showAvatar() })

  const { seconds } = createdAt

  const milliseconds = seconds * 1000

  const showNewMessageIndicator = unreadMessageIndex === index

  const showDateDivider = previousMessage ? isAfterTimeInterval({ currentMessage: message, previousMessage, timeInterval: 15 }) : false

  const attemptSendWithLocation = () => {
    const isEngageMessage = message?.suggestions?.some(({ reply: { text } }) => text === 'response-hidden')

    const includeLocation = message?.suggestions?.some(({ reply: { include_location: includeLocation } }) => includeLocation)

    if (isEngageMessage) {
      const message = {
        location: null,
        text: 'response-hidden',
        type: 'response-hidden'
      }

      if (includeLocation) {
        dispatch({
          type: ATTEMPT_MESSAGE_SEND_WITH_LOCATION,
          payload: {
            message,
            messageGuid,
            messageId
          }
        })
      } else {
        dispatch({ type: ON_MESSAGE_SEND, payload: { message, messageGuid, messageId } })
      }
    }
  }

  const markRead = () => {
    setTimeout(() => {
      const data = {
        guid: messageGuid,
        doc: messageId
      }

      dispatch(markMessageReadAction({
        apiKey,
        hash: userId,
        data
      }))

      attemptSendWithLocation()
    }, 3000)
  }

  const onChange = inView => {
    if (inView) {
      setIsVisible(inView)
    }
    if (inView && !read) {
      markRead()
    }
  }

  const handleOnClick = (suggestionIndex) => {
    setPressedSuggestionIndex(suggestionIndex)
  }

  useEffect(() => {
    if (hasResponse) setPressedSuggestionIndex(-1)
  }, [hasResponse])

  return (
    <div data-testid='message-bubble'>
      {showNewMessageIndicator && <NewMessageIndicator />}
      {showDateDivider && <div className={classes.dateSeparator}>{dayjs(milliseconds).locale(intl.locale).format('ll')}</div>}
      <InView
        onChange={onChange}
        threshold={0.3}
      >
        <div className={classes.messageWrapper}>
          <div className={classes.messageContainer}>
            <figure className={classes.avatar}>
              <img
                alt='avatar'
                src={avatar}
              />
            </figure>
            <div className={classes.content}>
              <div className={classes.message}>
                <ChatBubbleFilter
                  isMe={isMe}
                  message={message}
                >
                  <ChatMessageFilter
                    isVisible={isVisible}
                    {...props}
                  />
                </ChatBubbleFilter>
              </div>
              {!nextMessageHasImage && (
                <ChatMessageTime
                  isMe={isMe}
                  location={location}
                  time={milliseconds}
                  type={type}
                />
              )}
            </div>
          </div>
          {!hasResponse &&
            <ChatMessageSuggestions
              messageGuid={messageGuid}
              messageId={messageId}
              onClick={handleOnClick}
              pressedSuggestionIndex={pressedSuggestionIndex}
              showAvatar={showAvatar()}
              suggestions={suggestions}
            />}
          {hasResponse &&
            <div className={classes.responseWrapper}>
              <ChatResponseMessage
                isVisible={isVisible}
                message={responseMessage}
              />
            </div>}
        </div>
      </InView>
    </div>
  )
}

ChatMessage.defaultProps = {
  nextMessage: {}
}

ChatMessage.propTypes = {
  index: PropTypes.number.isRequired,
  nextMessage: PropTypes.object,
  message: PropTypes.shape({
    text: PropTypes.string
  }).isRequired
}

export default ChatMessage
